import qs from "qs";
import { soddApi } from "./soddApi";

function formatParams(params) {
    return qs.stringify(params, {
        arrayFormat: "brackets",
        skipNulls: true,
        filter: (prefix, value) => value || undefined,
    });
}

export const kpiApi = soddApi
    .enhanceEndpoints({
        addTagTypes: [
            "kpi",
            "kpi.dataSegment",
            "kpi.responsibleGroup",
            "kpi.options",
            "kpi.editInformationForm",
            "kpi.search", // useless ?
            "kpi.tracking",
            "kpi.relation",
        ],
    })
    .injectEndpoints({
        endpoints: (build) => ({
            createNewKpi: build.mutation({
                query: ({ data }) => ({
                    url: "api/v1/kpi",
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi"],
            }),
            getKpiList: build.query({
                query: ({ page, code, type, title, responsibleGroup }) => ({
                    url: "api/v1/kpi/list",
                    params: { page, code, type, title, responsibleGroup },
                }),
                providesTags: ["kpi"],
            }),
            searchKpi: build.query({
                query: (params) => ({
                    url: "api/v1/kpi/search",
                    params: formatParams(params),
                }),
                providesTags: (result, error, args) => {
                    return Boolean(result?.data)
                        ? [
                              "kpi.search",
                              ...result.data.map((kpi) => ({
                                  type: "kpi.search",
                                  id: kpi.id,
                              })),
                          ]
                        : ["kpi.search"];
                },
            }),
            getKpiById: build.query({
                query: ({ id }) => ({ url: `api/v1/kpi/${id}` }),
                providesTags: (result, error, arg) => [{ type: "kpi", id: arg.id ?? "*" }],
            }),
            deleteKpiById: build.mutation({
                query: ({ id }) => ({
                    url: `api/v1/kpi/${id}/delete`,
                    method: "POST",
                }),
                invalidatesTags: [
                    "kpi",
                    "kpi.dataSegment",
                    "kpi.responsibleGroup",
                    "kpi.editInformationForm",
                    "kpi.search",
                ],
            }),
            getKpiResponsibleGroup: build.query({
                query: (params) => ({
                    url: `api/v1/kpi/responsible-group`,
                    params: formatParams(params),
                }),
                providesTags: ["kpi.responsibleGroup"],
            }),
            getEditExpectedResultForm: build.query({
                query: ({ kpiId, kpiDataSegmentId }) => ({
                    url: `api/v1/kpi/${kpiId}/data-segment/${kpiDataSegmentId}/edit-expected-result-form`,
                }),
                providesTags: ["kpi.dataSegment"],
            }),
            updateKpiDataSegmentExpectedResult: build.mutation({
                query: ({ kpiId, kpiDataSegmentId, data }) => ({
                    url: `api/v1/kpi/${kpiId}/data-segment/${kpiDataSegmentId}/update-expected-result`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi", "kpi.dataSegment"],
            }),
            updateKpiDataSegmentResult: build.mutation({
                query: ({ kpiId, kpiDataSegmentId, data }) => ({
                    url: `api/v1/kpi/${kpiId}/data-segment/${kpiDataSegmentId}/update-result`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi", "kpi.dataSegment"],
            }),
            createNewKpiDataSegment: build.mutation({
                query: ({ kpiId, data }) => ({
                    url: `api/v1/kpi/${kpiId}/data-segment`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi", "kpi.dataSegment"],
            }),
            updateKpiDataSegment: build.mutation({
                query: ({ kpiId, kpiDataSegmentId, data }) => ({
                    url: `api/v1/kpi/${kpiId}/data-segment/${kpiDataSegmentId}/update`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi", "kpi.dataSegment"],
            }),
            deleteKpiDataSegment: build.mutation({
                query: ({ kpiId, kpiDataSegmentId }) => ({
                    url: `api/v1/kpi/${kpiId}/data-segment/${kpiDataSegmentId}/delete`,
                    method: "POST",
                }),
                invalidatesTags: ["kpi", "kpi.dataSegment"],
            }),
            getKpiOptions: build.query({
                query: () => ({
                    url: "api/v1/kpi/kpi-options",
                }),
                providesTags: ["kpi.options"],
            }),
            getKpiEditInformationForm: build.query({
                query: ({ kpiId }) => ({
                    url: `api/v1/kpi/${kpiId}/edit-information-form`,
                }),
                providesTags: ["kpi.editInformationForm"],
            }),
            updateKpiInformation: build.mutation({
                query: ({ kpiId, data }) => ({
                    url: `api/v1/kpi/${kpiId}/information`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi", "kpi.editInformationForm"],
            }),
            addKpiStakeholder: build.mutation({
                query: ({ id, ...data }) => ({
                    url: `api/v1/kpi/${id}/add-stakeholder`,
                    method: "POST",
                    body: { id, ...data },
                }),
                invalidatesTags: ["kpi"],
            }),
            deleteKpiStakeholder: build.mutation({
                query: ({ id, ...data }) => ({
                    url: `api/v1/kpi/${id}/delete-stakeholder`,
                    method: "POST",
                    body: { id, ...data },
                }),
                invalidatesTags: ["kpi"],
            }),
            /****************
             * kpi.relation *
             ***************/
            getKpiRelationOptions: build.query({
                query: ({ kpiId }) => ({
                    url: `api/v1/kpi/${kpiId}/relations/create`,
                    method: "GET",
                }),
            }),
            createKpiRelation: build.mutation({
                query: ({ kpiId, data }) => ({
                    url: `api/v1/kpi/${kpiId}/relations`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi", "kpi.relation"],
            }),
            deleteKpiRelationById: build.mutation({
                query: ({ relationId }) => ({
                    url: `api/v1/kpi/relations/${relationId}`,
                    method: "DELETE",
                }),
                invalidatesTags: ["kpi", "kpi.relation"],
            }),
            /****************
             * kpi.tracking *
             ***************/
            getKpiTrackingOptions: build.query({
                query: ({ kpiId }) => ({
                    url: `api/v1/kpi/${kpiId}/trackings/create`,
                    method: "GET",
                }),
            }),
            createKpiTracking: build.mutation({
                query: ({ kpiId, data }) => ({
                    url: `api/v1/kpi/${kpiId}/trackings`,
                    method: "POST",
                    body: data,
                }),
                invalidatesTags: ["kpi", "kpi.tracking"],
            }),
            deleteKpiTrackingById: build.mutation({
                query: ({ trackingId }) => ({
                    url: `api/v1/kpi/trackings/${trackingId}`,
                    method: "DELETE",
                }),
                invalidatesTags: ["kpi", "kpi.tracking"],
            }),
        }),
    });
